import React,{useEffect,useMemo,useState}from"react";
import{Box,Button,Dialog,DialogActions,DialogContent,DialogTitle,FormControl,InputLabel,MenuItem,Paper,Select,Table,TableBody,TableCell,TableContainer,TableHead,TablePagination,TableRow,TextField,Typography,useMediaQuery}from"@mui/material";
import{useNavigate}from"react-router-dom";
import Sidebar from"./AdminPanel/Sidebar";
import TopBar from"./AdminPanel/TopBar";
import{useDashboardTheme}from"../../context/DashboardThemeContext";
import{useAuth}from"../../context/AuthContext";
import{useAlert}from"../../context/AlertContext";

const API_BASE="/api/treasury/conciliaciones";
const today=()=>new Date().toISOString().slice(0,10);

function getToken(){
  return localStorage.getItem("accessToken")||localStorage.getItem("access")||"";
}

function userCan(user,code){
  if(!user)return false;
  if(user.role==="super_admin"||user.role==="admin")return true;
  return(user.permissions||[]).includes(code);
}

function number(value){
  const result=Number(String(value??"0").replace(",","."));
  return Number.isFinite(result)?result:0;
}

function money(value){
  return number(value).toFixed(2);
}

function signedMoney(value){
  const amount=number(value);
  if(amount>0)return`+$${money(amount)}`;
  if(amount<0)return`-$${money(Math.abs(amount))}`;
  return"$0.00";
}

function normalizeMoney(value){
  let text=String(value??"").replace(/,/g,".");
  text=text.replace(/[^\d.]/g,"");
  const firstDot=text.indexOf(".");
  if(firstDot!==-1){
    text=text.slice(0,firstDot+1)+text.slice(firstDot+1).replace(/\./g,"");
  }
  if(text.startsWith("."))text=`0${text}`;
  if(!text)return"";
  const parts=text.split(".");
  let integer=parts[0].replace(/^0+(?=\d)/,"");
  if(parts.length>1)return`${integer||"0"}.${parts[1].slice(0,2)}`;
  return integer;
}

function formatDate(value){
  if(!value)return"-";
  const parts=String(value).split("-");
  if(parts.length===3)return`${parts[2]}/${parts[1]}/${parts[0]}`;
  return value;
}

const emptyForm=()=>({
  fecha:today(),
  saldo_real_efectivo:"",
  saldo_real_transferencia:"",
  motivo:"",
  observacion:"",
});

export default function AdminReconciliation(){
  const[sidebarOpen,setSidebarOpen]=useState(true);
  const{dashboardTheme}=useDashboardTheme();
  const{user}=useAuth();
  const{showError,showSuccess,showInfo}=useAlert();
  const navigate=useNavigate();
  const isMobile=useMediaQuery("(max-width:900px)");
  const token=getToken();

  const[items,setItems]=useState([]);
  const[loading,setLoading]=useState(false);
  const[page,setPage]=useState(0);
  const[totalItems,setTotalItems]=useState(0);
  const[search,setSearch]=useState("");
  const[statusFilter,setStatusFilter]=useState("");
  const[fromDate,setFromDate]=useState("");
  const[toDate,setToDate]=useState("");

  const[currentBalances,setCurrentBalances]=useState({
    efectivo:"0.00",
    transferencia:"0.00",
    total:"0.00",
  });

  const[createOpen,setCreateOpen]=useState(false);
  const[creating,setCreating]=useState(false);
  const[form,setForm]=useState(emptyForm());

  const[detailOpen,setDetailOpen]=useState(false);
  const[detailLoading,setDetailLoading]=useState(false);
  const[detail,setDetail]=useState(null);

  const[annulOpen,setAnnulOpen]=useState(false);
  const[annulling,setAnnulling]=useState(false);
  const[annulItem,setAnnulItem]=useState(null);
  const[annulReason,setAnnulReason]=useState("");

  const rowsPerPage=isMobile?10:30;

  const canView=userCan(user,"conciliacion.view");
  const canCreate=userCan(user,"conciliacion.create");
  const canDelete=userCan(user,"conciliacion.delete");

  const headers={
    Authorization:`Bearer ${token}`,
    Accept:"application/json",
  };

  const parseError=data=>{
    const error=data?.error||data?.detail||data?.mensaje;
    if(!error)return"No se pudo procesar la solicitud.";
    if(typeof error==="string")return error;
    if(Array.isArray(error))return error.join(" ");
    if(typeof error==="object")return Object.values(error).flat().join(" ");
    return"No se pudo procesar la solicitud.";
  };

  const differenceCash=useMemo(
    ()=>number(form.saldo_real_efectivo)-number(currentBalances.efectivo),
    [form.saldo_real_efectivo,currentBalances.efectivo]
  );

  const differenceTransfer=useMemo(
    ()=>number(form.saldo_real_transferencia)-number(currentBalances.transferencia),
    [form.saldo_real_transferencia,currentBalances.transferencia]
  );

  const realTotal=useMemo(
    ()=>number(form.saldo_real_efectivo)+number(form.saldo_real_transferencia),
    [form.saldo_real_efectivo,form.saldo_real_transferencia]
  );

  const differenceTotal=useMemo(
    ()=>differenceCash+differenceTransfer,
    [differenceCash,differenceTransfer]
  );

  const loadBalances=async()=>{
    if(!canView)return null;
    try{
      const response=await fetch(`${API_BASE}/saldos/`,{headers});
      const data=await response.json();
      if(!response.ok){
        showError(parseError(data));
        return null;
      }

      const balances={
        efectivo:data.efectivo||"0.00",
        transferencia:data.transferencia||"0.00",
        total:data.total||"0.00",
      };

      setCurrentBalances(balances);
      return balances;
    }catch{
      showError("Error de conexión al consultar los saldos.");
      return null;
    }
  };

  const loadItems=async()=>{
    if(!canView)return;

    try{
      setLoading(true);

      const params=new URLSearchParams();
      params.append("page",String(page+1));
      params.append("page_size",String(rowsPerPage));

      if(search.trim())params.append("q",search.trim());
      if(statusFilter)params.append("estado",statusFilter);
      if(fromDate)params.append("fecha_desde",fromDate);
      if(toDate)params.append("fecha_hasta",toDate);

      const response=await fetch(`${API_BASE}/?${params.toString()}`,{headers});
      const data=await response.json();

      if(!response.ok){
        showError(parseError(data));
        return;
      }

      setItems(data.results||[]);
      setTotalItems(data.pagination?.total_items||0);

      if(data.pagination?.page&&data.pagination.page-1!==page){
        setPage(data.pagination.page-1);
      }
    }catch{
      showError("Error de conexión al cargar las conciliaciones.");
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    const delay=setTimeout(()=>{
      setPage(0);
      if(canView)loadBalances();
    },350);

    return()=>clearTimeout(delay);
  },[canView]);

  useEffect(()=>{
    const delay=setTimeout(()=>{
      loadItems();
    },350);

    return()=>clearTimeout(delay);
  },[page,rowsPerPage,search,statusFilter,fromDate,toDate,canView]);

  const openCreate=async()=>{
    const balances=await loadBalances();
    if(!balances)return;

    setForm({
      fecha:today(),
      saldo_real_efectivo:money(balances.efectivo),
      saldo_real_transferencia:money(balances.transferencia),
      motivo:"",
      observacion:"",
    });

    setCreateOpen(true);
  };

  const closeCreate=()=>{
    if(creating)return;
    setCreateOpen(false);
    setForm(emptyForm());
  };

  const handleForm=(field,value)=>{
    setForm(prev=>({
      ...prev,
      [field]:field==="saldo_real_efectivo"||field==="saldo_real_transferencia"
        ?normalizeMoney(value)
        :value,
    }));
  };

  const createReconciliation=async()=>{
    if(creating)return;

    if(!form.fecha){
      showInfo("Debe seleccionar la fecha.");
      return;
    }

    if(form.saldo_real_efectivo===""){
      showInfo("Debe indicar el efectivo real.");
      return;
    }

    if(form.saldo_real_transferencia===""){
      showInfo("Debe indicar el saldo real de transferencias.");
      return;
    }

    if(number(form.saldo_real_efectivo)<0||number(form.saldo_real_transferencia)<0){
      showInfo("Los saldos reales no pueden ser negativos.");
      return;
    }

    if(!form.motivo.trim()){
      showInfo("Debe indicar el motivo de la conciliación.");
      return;
    }

    try{
      setCreating(true);

      const response=await fetch(`${API_BASE}/crear/`,{
        method:"POST",
        headers:{
          ...headers,
          "Content-Type":"application/json",
        },
        body:JSON.stringify({
          fecha:form.fecha,
          saldo_real_efectivo:form.saldo_real_efectivo||"0",
          saldo_real_transferencia:form.saldo_real_transferencia||"0",
          motivo:form.motivo.trim(),
          observacion:form.observacion.trim(),
        }),
      });

      const data=await response.json();

      if(!response.ok){
        showError(parseError(data));
        return;
      }

      showSuccess(data.mensaje||"Conciliación registrada correctamente.");
      setCreateOpen(false);
      setForm(emptyForm());

      if(data.saldos){
        setCurrentBalances({
          efectivo:data.saldos.efectivo||"0.00",
          transferencia:data.saldos.transferencia||"0.00",
          total:data.saldos.total||"0.00",
        });
      }else{
        await loadBalances();
      }

      setPage(0);
      await loadItems();
    }catch{
      showError("Error de conexión al registrar la conciliación.");
    }finally{
      setCreating(false);
    }
  };

  const openDetail=async item=>{
    try{
      setDetailOpen(true);
      setDetailLoading(true);
      setDetail(null);

      const response=await fetch(`${API_BASE}/${item.id}/`,{headers});
      const data=await response.json();

      if(!response.ok){
        showError(parseError(data));
        setDetailOpen(false);
        return;
      }

      setDetail(data.conciliacion||null);
    }catch{
      showError("Error de conexión al cargar la conciliación.");
      setDetailOpen(false);
    }finally{
      setDetailLoading(false);
    }
  };

  const closeDetail=()=>{
    setDetailOpen(false);
    setDetail(null);
  };

  const openAnnul=item=>{
    setAnnulItem(item);
    setAnnulReason("");
    setAnnulOpen(true);
  };

  const closeAnnul=()=>{
    if(annulling)return;
    setAnnulOpen(false);
    setAnnulItem(null);
    setAnnulReason("");
  };

  const annulReconciliation=async()=>{
    if(!annulItem||annulling)return;

    if(!annulReason.trim()){
      showInfo("Debe indicar el motivo de la anulación.");
      return;
    }

    try{
      setAnnulling(true);

      const response=await fetch(`${API_BASE}/${annulItem.id}/anular/`,{
        method:"POST",
        headers:{
          ...headers,
          "Content-Type":"application/json",
        },
        body:JSON.stringify({
          motivo:annulReason.trim(),
        }),
      });

      const data=await response.json();

      if(!response.ok){
        showError(parseError(data));
        return;
      }

      showSuccess(data.mensaje||"Conciliación anulada correctamente.");
      closeAnnul();

      if(data.saldos){
        setCurrentBalances({
          efectivo:data.saldos.efectivo||"0.00",
          transferencia:data.saldos.transferencia||"0.00",
          total:data.saldos.total||"0.00",
        });
      }else{
        await loadBalances();
      }

      await loadItems();
    }catch{
      showError("Error de conexión al anular la conciliación.");
    }finally{
      setAnnulling(false);
    }
  };

  const outlinedButtonSx={
    minHeight:40,
    px:2.4,
    borderRadius:"12px",
    textTransform:"none",
    fontWeight:700,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{
      borderColor:dashboardTheme.button_color,
      bgcolor:`${dashboardTheme.button_color}12`,
      boxShadow:"none",
    },
  };

  const smallButtonSx={
    minHeight:32,
    px:1.5,
    borderRadius:"10px",
    textTransform:"none",
    fontWeight:700,
    fontSize:12,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{
      borderColor:dashboardTheme.button_color,
      bgcolor:`${dashboardTheme.button_color}12`,
      boxShadow:"none",
    },
  };

  const dangerButtonSx={
    minHeight:32,
    px:1.5,
    borderRadius:"10px",
    textTransform:"none",
    fontWeight:700,
    fontSize:12,
    color:"#dc2626",
    borderColor:"#dc2626",
    boxShadow:"none",
    "&:hover":{
      borderColor:"#dc2626",
      bgcolor:"rgba(220,38,38,0.08)",
      boxShadow:"none",
    },
  };

  const dialogPaperProps={
    elevation:0,
    sx:{
      width:"100%",
      maxWidth:650,
      borderRadius:"16px",
      border:`1px solid ${dashboardTheme.border_color}`,
      bgcolor:dashboardTheme.card_background,
      color:dashboardTheme.text_color,
      boxShadow:"none",
    },
  };

  const selectMenuProps={
    disableScrollLock:true,
    PaperProps:{
      elevation:0,
      sx:{
        mt:.5,
        bgcolor:dashboardTheme.card_background,
        color:dashboardTheme.text_color,
        border:`1px solid ${dashboardTheme.border_color}`,
        boxShadow:"none",
      },
    },
  };

  const balanceCard=(title,value)=>(
    <Paper elevation={0} sx={{p:1.6,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",bgcolor:dashboardTheme.card_background}}>
      <Typography sx={{fontSize:12.5,fontWeight:500,color:dashboardTheme.muted_text_color}}>{title}</Typography>
      <Typography sx={{fontSize:22,fontWeight:700,color:dashboardTheme.text_color,mt:.3}}>${money(value)}</Typography>
    </Paper>
  );

  return(
    <Box sx={{minHeight:"100vh",bgcolor:dashboardTheme.dashboard_background,display:"flex",color:dashboardTheme.text_color,overflowX:"hidden"}}>
      <Sidebar open={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>

      <Box sx={{flex:1,minWidth:0,minHeight:"100vh",display:"flex",flexDirection:"column"}}>
        <TopBar sidebarOpen={sidebarOpen} onToggleSidebar={()=>setSidebarOpen(prev=>!prev)}/>

        <Box component="main" sx={{flex:1,width:"100%",p:{xs:1.5,sm:2,md:3}}}>
          <Box sx={{width:{xs:"100%",lg:"95%"},mx:"auto",mt:{xs:1,md:1.5}}}>

            <Box sx={{display:"flex",alignItems:{xs:"stretch",sm:"center"},justifyContent:"space-between",flexDirection:{xs:"column",sm:"row"},gap:1.5,mb:2}}>
              <Box>
                <Typography sx={{fontSize:{xs:24,sm:26,md:30},fontWeight:600,color:dashboardTheme.primary_color,lineHeight:1.2}}>
                  Conciliaciones
                </Typography>
                <Typography sx={{fontSize:13,color:dashboardTheme.muted_text_color,mt:.4}}>
                  Ajuste y control de saldos reales de Tesorería
                </Typography>
              </Box>

              <Box sx={{display:"flex",gap:1,flexWrap:"wrap"}}>
                <Button variant="outlined" onClick={()=>navigate("/admin-panel/tesoreria")} sx={outlinedButtonSx}>
                  Tesorería
                </Button>

                {canCreate&&(
                  <Button variant="contained" onClick={openCreate} sx={dashboardTheme.buttonStyles.contained}>
                    Nueva conciliación
                  </Button>
                )}
              </Box>
            </Box>

            {canView&&(
              <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(3,minmax(0,1fr))"},gap:1.2,mb:2}}>
                {balanceCard("Efectivo actual",currentBalances.efectivo)}
                {balanceCard("Transferencias actuales",currentBalances.transferencia)}
                {balanceCard("Disponible total",currentBalances.total)}
              </Box>
            )}

            <Paper elevation={0} sx={dashboardTheme.tableStyles.wrapper}>
              <Box sx={{...dashboardTheme.tableStyles.toolbar,display:"grid",gridTemplateColumns:{xs:"1fr",md:"minmax(250px,1.5fr) repeat(3,minmax(150px,.7fr))"},gap:1.2}}>
                <TextField
                  fullWidth
                  size="small"
                  label="Buscar por motivo, observación o código"
                  value={search}
                  onChange={event=>{
                    setSearch(event.target.value);
                    setPage(0);
                  }}
                  sx={dashboardTheme.inputStyles}
                />

                <FormControl fullWidth size="small" sx={dashboardTheme.inputStyles}>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    label="Estado"
                    value={statusFilter}
                    onChange={event=>{
                      setStatusFilter(event.target.value);
                      setPage(0);
                    }}
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="ACTIVO">Activas</MenuItem>
                    <MenuItem value="ANULADO">Anuladas</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Desde"
                  value={fromDate}
                  onChange={event=>{
                    setFromDate(event.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{shrink:true}}
                  sx={dashboardTheme.inputStyles}
                />

                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Hasta"
                  value={toDate}
                  onChange={event=>{
                    setToDate(event.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{shrink:true}}
                  sx={dashboardTheme.inputStyles}
                />
              </Box>

              <Box sx={dashboardTheme.tableStyles.tableArea}>
                <TableContainer sx={dashboardTheme.tableStyles.container}>
                  <Table size="small" sx={{...dashboardTheme.tableStyles.table,minWidth:1150}}>
                    <TableHead>
                      <TableRow sx={dashboardTheme.tableStyles.headRow}>
                        <TableCell sx={dashboardTheme.tableStyles.headCell}>Fecha</TableCell>
                        <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:240}}>Motivo</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Efectivo sistema</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Efectivo real</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Diferencia</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Transferencia sistema</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Transferencia real</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Diferencia</TableCell>
                        <TableCell sx={dashboardTheme.tableStyles.headCell}>Estado</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Acciones</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {items.map(item=>(
                        <TableRow key={item.id} hover sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell sx={{whiteSpace:"nowrap"}}>{formatDate(item.fecha)}</TableCell>

                          <TableCell>
                            <Typography sx={{fontSize:13.5,fontWeight:700,color:dashboardTheme.text_color}}>
                              {item.motivo||"-"}
                            </Typography>
                            <Typography sx={{fontSize:12,color:dashboardTheme.muted_text_color,mt:.25}}>
                              {item.id}
                            </Typography>
                          </TableCell>

                          <TableCell align="right">${money(item.saldo_sistema_efectivo)}</TableCell>
                          <TableCell align="right">${money(item.saldo_real_efectivo)}</TableCell>

                          <TableCell align="right" sx={{fontWeight:700,color:number(item.diferencia_efectivo)<0?"#dc2626":number(item.diferencia_efectivo)>0?"#15803d":dashboardTheme.text_color}}>
                            {signedMoney(item.diferencia_efectivo)}
                          </TableCell>

                          <TableCell align="right">${money(item.saldo_sistema_transferencia)}</TableCell>
                          <TableCell align="right">${money(item.saldo_real_transferencia)}</TableCell>

                          <TableCell align="right" sx={{fontWeight:700,color:number(item.diferencia_transferencia)<0?"#dc2626":number(item.diferencia_transferencia)>0?"#15803d":dashboardTheme.text_color}}>
                            {signedMoney(item.diferencia_transferencia)}
                          </TableCell>

                          <TableCell>
                            <Typography sx={{fontSize:13,fontWeight:700,color:item.estado==="ANULADO"?"#dc2626":dashboardTheme.text_color}}>
                              {item.estado==="ANULADO"?"Anulada":"Activa"}
                            </Typography>
                          </TableCell>

                          <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                            <Box sx={{display:"flex",justifyContent:"flex-end",gap:1}}>
                              <Button variant="outlined" size="small" onClick={()=>openDetail(item)} sx={smallButtonSx}>
                                Ver
                              </Button>

                              {canDelete&&item.estado!=="ANULADO"&&(
                                <Button variant="outlined" size="small" onClick={()=>openAnnul(item)} sx={dangerButtonSx}>
                                  Anular
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}

                      {loading&&(
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={dashboardTheme.tableStyles.emptyCell}>
                            Cargando conciliaciones...
                          </TableCell>
                        </TableRow>
                      )}

                      {!loading&&items.length===0&&canView&&(
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={dashboardTheme.tableStyles.emptyCell}>
                            No existen conciliaciones registradas
                          </TableCell>
                        </TableRow>
                      )}

                      {!canView&&(
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={dashboardTheme.tableStyles.emptyCell}>
                            No tienes permisos para ver conciliaciones
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <TablePagination
                component="div"
                count={totalItems}
                page={page}
                onPageChange={(_event,newPage)=>setPage(newPage)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}
                labelDisplayedRows={({from,to,count})=>`${from}-${to} de ${count}`}
                sx={dashboardTheme.tableStyles.pagination}
              />
            </Paper>
          </Box>
        </Box>
      </Box>

      <Dialog open={createOpen} onClose={closeCreate} fullWidth maxWidth="md" PaperProps={dialogPaperProps}>
        <DialogTitle sx={{fontWeight:700,color:dashboardTheme.text_color}}>
          Nueva conciliación
        </DialogTitle>

        <DialogContent>
          <Typography sx={{fontSize:13.5,color:dashboardTheme.muted_text_color,mb:2}}>
            Indica cuánto dinero existe realmente. El sistema calculará automáticamente los ajustes necesarios.
          </Typography>

          <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(3,minmax(0,1fr))"},gap:1.2,mb:2}}>
            {balanceCard("Efectivo sistema",currentBalances.efectivo)}
            {balanceCard("Transferencias sistema",currentBalances.transferencia)}
            {balanceCard("Total sistema",currentBalances.total)}
          </Box>

          <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"repeat(2,minmax(0,1fr))"},gap:1.5}}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Fecha"
              value={form.fecha}
              onChange={event=>handleForm("fecha",event.target.value)}
              InputLabelProps={{shrink:true}}
              sx={dashboardTheme.inputStyles}
            />

            <TextField
              fullWidth
              size="small"
              label="Motivo"
              value={form.motivo}
              onChange={event=>handleForm("motivo",event.target.value)}
              sx={dashboardTheme.inputStyles}
            />

            <TextField
              fullWidth
              size="small"
              label="Efectivo real"
              value={form.saldo_real_efectivo}
              inputProps={{inputMode:"decimal"}}
              onChange={event=>handleForm("saldo_real_efectivo",event.target.value)}
              sx={dashboardTheme.inputStyles}
            />

            <TextField
              fullWidth
              size="small"
              label="Transferencias reales"
              value={form.saldo_real_transferencia}
              inputProps={{inputMode:"decimal"}}
              onChange={event=>handleForm("saldo_real_transferencia",event.target.value)}
              sx={dashboardTheme.inputStyles}
            />
          </Box>

          <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(3,minmax(0,1fr))"},gap:1.2,mt:1.5}}>
            <Paper elevation={0} sx={{p:1.5,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",bgcolor:dashboardTheme.dashboard_background}}>
              <Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Ajuste efectivo</Typography>
              <Typography sx={{fontSize:19,fontWeight:700,color:differenceCash<0?"#dc2626":differenceCash>0?"#15803d":dashboardTheme.text_color}}>
                {signedMoney(differenceCash)}
              </Typography>
            </Paper>

            <Paper elevation={0} sx={{p:1.5,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",bgcolor:dashboardTheme.dashboard_background}}>
              <Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Ajuste transferencia</Typography>
              <Typography sx={{fontSize:19,fontWeight:700,color:differenceTransfer<0?"#dc2626":differenceTransfer>0?"#15803d":dashboardTheme.text_color}}>
                {signedMoney(differenceTransfer)}
              </Typography>
            </Paper>

            <Paper elevation={0} sx={{p:1.5,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",bgcolor:dashboardTheme.dashboard_background}}>
              <Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Total real</Typography>
              <Typography sx={{fontSize:19,fontWeight:700,color:dashboardTheme.text_color}}>
                ${money(realTotal)}
              </Typography>
              <Typography sx={{fontSize:11.5,color:dashboardTheme.muted_text_color}}>
                Diferencia total: {signedMoney(differenceTotal)}
              </Typography>
            </Paper>
          </Box>

          <TextField
            fullWidth
            size="small"
            multiline
            minRows={3}
            label="Observación"
            value={form.observacion}
            onChange={event=>handleForm("observacion",event.target.value)}
            sx={{mt:1.5,...dashboardTheme.inputStyles}}
          />
        </DialogContent>

        <DialogActions sx={{px:3,pb:2.5,gap:1}}>
          <Button variant="outlined" onClick={closeCreate} disabled={creating} sx={outlinedButtonSx}>
            Cancelar
          </Button>

          <Button variant="contained" onClick={createReconciliation} disabled={creating} sx={dashboardTheme.buttonStyles.contained}>
            {creating?"Guardando...":"Guardar conciliación"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={closeDetail} fullWidth maxWidth="md" PaperProps={dialogPaperProps}>
        <DialogTitle sx={{fontWeight:700,color:dashboardTheme.text_color}}>
          Detalle de conciliación
        </DialogTitle>

        <DialogContent>
          {detailLoading&&(
            <Typography sx={{py:3,textAlign:"center",color:dashboardTheme.muted_text_color}}>
              Cargando...
            </Typography>
          )}

          {!detailLoading&&detail&&(
            <>
              <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(2,minmax(0,1fr))"},gap:1.2}}>
                <Paper elevation={0} sx={{p:1.5,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",bgcolor:dashboardTheme.dashboard_background}}>
                  <Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Fecha</Typography>
                  <Typography sx={{fontWeight:700}}>{formatDate(detail.fecha)}</Typography>
                </Paper>

                <Paper elevation={0} sx={{p:1.5,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",bgcolor:dashboardTheme.dashboard_background}}>
                  <Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Estado</Typography>
                  <Typography sx={{fontWeight:700,color:detail.estado==="ANULADO"?"#dc2626":dashboardTheme.text_color}}>
                    {detail.estado==="ANULADO"?"Anulada":"Activa"}
                  </Typography>
                </Paper>
              </Box>

              <Typography sx={{fontSize:17,fontWeight:700,mt:2,mb:1}}>Efectivo</Typography>

              <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(3,minmax(0,1fr))"},gap:1.2}}>
                {balanceCard("Sistema",detail.saldo_sistema_efectivo)}
                {balanceCard("Real",detail.saldo_real_efectivo)}

                <Paper elevation={0} sx={{p:1.6,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",bgcolor:dashboardTheme.card_background}}>
                  <Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Diferencia</Typography>
                  <Typography sx={{fontSize:22,fontWeight:700,color:number(detail.diferencia_efectivo)<0?"#dc2626":number(detail.diferencia_efectivo)>0?"#15803d":dashboardTheme.text_color}}>
                    {signedMoney(detail.diferencia_efectivo)}
                  </Typography>
                </Paper>
              </Box>

              <Typography sx={{fontSize:17,fontWeight:700,mt:2,mb:1}}>Transferencias</Typography>

              <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(3,minmax(0,1fr))"},gap:1.2}}>
                {balanceCard("Sistema",detail.saldo_sistema_transferencia)}
                {balanceCard("Real",detail.saldo_real_transferencia)}

                <Paper elevation={0} sx={{p:1.6,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",bgcolor:dashboardTheme.card_background}}>
                  <Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Diferencia</Typography>
                  <Typography sx={{fontSize:22,fontWeight:700,color:number(detail.diferencia_transferencia)<0?"#dc2626":number(detail.diferencia_transferencia)>0?"#15803d":dashboardTheme.text_color}}>
                    {signedMoney(detail.diferencia_transferencia)}
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{mt:2}}>
                <Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Motivo</Typography>
                <Typography sx={{fontSize:14,fontWeight:700,color:dashboardTheme.text_color}}>
                  {detail.motivo||"-"}
                </Typography>
              </Box>

              <Box sx={{mt:1.5}}>
                <Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Observación</Typography>
                <Typography sx={{fontSize:14,color:dashboardTheme.text_color}}>
                  {detail.observacion||"-"}
                </Typography>
              </Box>

              <Box sx={{mt:1.5}}>
                <Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Usuario</Typography>
                <Typography sx={{fontSize:14,color:dashboardTheme.text_color}}>
                  {detail.usuario||"-"}
                </Typography>
              </Box>

              {detail.estado==="ANULADO"&&(
                <Paper elevation={0} sx={{mt:2,p:1.5,border:"1px solid rgba(220,38,38,.35)",borderRadius:"12px",bgcolor:"rgba(220,38,38,.04)"}}>
                  <Typography sx={{fontSize:12.5,color:"#dc2626"}}>Motivo de anulación</Typography>
                  <Typography sx={{fontSize:14,fontWeight:600,color:dashboardTheme.text_color}}>
                    {detail.motivo_anulacion||"-"}
                  </Typography>
                </Paper>
              )}

              <Typography sx={{fontSize:17,fontWeight:700,mt:2.5,mb:1}}>Movimientos generados</Typography>

              <TableContainer sx={{border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",overflowX:"auto"}}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={dashboardTheme.tableStyles.headRow}>
                      <TableCell sx={dashboardTheme.tableStyles.headCell}>Fondo</TableCell>
                      <TableCell sx={dashboardTheme.tableStyles.headCell}>Tipo</TableCell>
                      <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Monto</TableCell>
                      <TableCell sx={dashboardTheme.tableStyles.headCell}>Estado</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {(detail.movimientos||[]).length?(
                      detail.movimientos.map(movement=>(
                        <TableRow key={movement.id} sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell>{movement.medio_nombre||movement.medio}</TableCell>
                          <TableCell>{movement.tipo_nombre||movement.tipo}</TableCell>
                          <TableCell align="right" sx={{fontWeight:700}}>${money(movement.monto)}</TableCell>
                          <TableCell>{movement.esta_revertido?"Revertido":"Activo"}</TableCell>
                        </TableRow>
                      ))
                    ):(
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={dashboardTheme.tableStyles.emptyCell}>
                          La conciliación no generó ajustes.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{px:3,pb:2.5}}>
          <Button variant="outlined" onClick={closeDetail} sx={outlinedButtonSx}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={annulOpen} onClose={closeAnnul} fullWidth maxWidth="xs" PaperProps={dialogPaperProps}>
        <DialogTitle sx={{fontWeight:700,color:dashboardTheme.text_color}}>
          Anular conciliación
        </DialogTitle>

        <DialogContent>
          <Typography sx={{fontSize:14,color:dashboardTheme.muted_text_color,mb:1.5}}>
            Los ajustes realizados por esta conciliación serán revertidos.
          </Typography>

          <TextField
            autoFocus
            fullWidth
            size="small"
            multiline
            minRows={3}
            label="Motivo de anulación"
            value={annulReason}
            onChange={event=>setAnnulReason(event.target.value)}
            sx={dashboardTheme.inputStyles}
          />
        </DialogContent>

        <DialogActions sx={{px:3,pb:2.5,gap:1}}>
          <Button variant="outlined" onClick={closeAnnul} disabled={annulling} sx={outlinedButtonSx}>
            Cancelar
          </Button>

          <Button variant="outlined" onClick={annulReconciliation} disabled={annulling} sx={dangerButtonSx}>
            {annulling?"Anulando...":"Anular"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}